import Shipment from '../../../types/api/shipment'
import Task, { TaskTypes } from '../../../types/api/task'
import renderDatetime from '../../tables/render-datetime'

export const ASSIGNMENT_APPROVAL_TASK_TYPE: TaskTypes = 'Согласование задания'

export type AssignmentApprovalAction = 'create' | 'reopen' | 'none'

const isOpenTask = (task: { taskStatus?: string | null }) =>
    task.taskStatus !== 'Завершена' && task.taskStatus !== 'Отменена'

/** Одна задача на отгрузку: создать, вернуть отклонённую или ничего не делать. */
export function getAssignmentApprovalAction(
    shipment?: Pick<Shipment, 'approved' | 'tasks'> | null
): AssignmentApprovalAction {
    const hasTask = shipment?.tasks?.some(task => task.type === ASSIGNMENT_APPROVAL_TASK_TYPE)
    if (!hasTask) return 'create'
    if (shipment?.approved === 'Отклонено') return 'reopen'
    return 'none'
}

/** В информации по отгрузке показываем одну задачу: открытую, иначе последнюю. */
export function pickAssignmentApprovalTask(tasks?: Task[] | null): Task | undefined {
    const approvalTasks = (tasks ?? []).filter(task => task.type === ASSIGNMENT_APPROVAL_TASK_TYPE)
    const open = approvalTasks.find(isOpenTask)
    if (open) return open

    return [...approvalTasks].sort((a, b) =>
        (b.createdAt || '').localeCompare(a.createdAt || '')
    )[0]
}

export type ApprovalTaskTone = 'waiting' | 'approved' | 'rejected' | 'closed'

/** Тон иконки. «closed» — задача закрыта, а решение отгрузки уже не про неё. */
export function getApprovalTaskTone(task: Task, shipment?: Shipment | null): ApprovalTaskTone {
    if (isOpenTask(task)) return 'waiting'

    const hasOpenSibling = shipment?.tasks?.some(
        other =>
            other.type === ASSIGNMENT_APPROVAL_TASK_TYPE &&
            other.documentId !== task.documentId &&
            isOpenTask(other)
    )
    if (!hasOpenSibling) {
        if (shipment?.approved === 'Согласован') return 'approved'
        if (shipment?.approved === 'Отклонено') return 'rejected'
    }

    return 'closed'
}

export function getApprovalTaskLabel(task: Task, shipment?: Shipment | null): string {
    switch (getApprovalTaskTone(task, shipment)) {
        case 'waiting':
            return 'Ожидает'
        case 'approved':
            return 'Согласовано'
        case 'rejected':
            return 'Отклонено. Причина в Telegram.'
        case 'closed':
            return task.taskStatus === 'Отменена' ? 'Отменена' : 'Завершена'
    }
}

/**
 * Legacy-отгрузки без задачи согласования и без статуса — не блокируются.
 * Отклонено блокирует даже если tasks не попали в populate.
 */
export function isShipmentAwaitingCuratorApproval(shipment?: Shipment | null): boolean {
    if (!shipment) return false

    if (shipment.approved === 'Согласован') {
        return false
    }

    if (shipment.approved === 'Отклонено') {
        return true
    }

    const hasApprovalTask = shipment.tasks?.some(
        task => task.type === ASSIGNMENT_APPROVAL_TASK_TYPE
    )
    if (!hasApprovalTask) {
        return false
    }

    return true
}

export function getShipmentApprovalLabel(shipment?: Shipment | null): string {
    if (!shipment) return '—'
    if (shipment.approved === 'Согласован') return 'Согласовано'
    if (shipment.approved === 'Отклонено') {
        return 'Отклонено. Причина в Telegram.'
    }
    if (shipment.tasks?.some(task => task.type === ASSIGNMENT_APPROVAL_TASK_TYPE)) {
        return 'Ожидает'
    }
    return '—'
}

/** Последняя (не отменённая) задача согласования задания */
export function getShipmentApprovalTask(shipment?: Shipment | null): Task | undefined {
    return shipment?.tasks
        ?.filter(
            task => task.type === ASSIGNMENT_APPROVAL_TASK_TYPE && task.taskStatus !== 'Отменена'
        )
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '') || b.id - a.id)[0]
}

/** Подсказка для тега «К»: какой куратор обрабатывал отгрузку */
export function getShipmentCuratorInfo(shipment?: Shipment | null): string {
    const task = getShipmentApprovalTask(shipment)
    if (!task) return 'Задачи согласования нет'

    const curator = task.performer?.username || '—'

    if (shipment?.approved) {
        const decision = shipment.approved === 'Согласован' ? 'согласовано' : 'отклонено'
        const date = task.endDate ? ` ${renderDatetime(task.endDate)}` : ''
        return `Куратор: ${curator} — ${decision}${date}`
    }

    return `Ожидает согласования: ${curator}`
}
